import { Box, chakra, Text, useBoolean } from '@chakra-ui/react';
import { IoAdd, IoPulse } from 'react-icons/io5';
import { useSelector } from 'react-redux';

import { Button } from 'components/v4';
import DashboardPanel from '../DashboardPanel';
import DashboardServices from '../../DashboardServices';
import useGetAvailableServiceProfiles from '../setupProgress/queries/useGetAvailableServiceProfiles';
import { dashboardTokens } from '../dashboardTokens';
import AvailableServices from './AvailableServices';
import SummaryTiles from './SummaryTiles';
import getAvailableServices from './getAvailableServices';
import useAccountSummaryCounts from './useAccountSummaryCounts';

// The Service status heading, which notifications about services scroll to.
export const SERVICE_STATUS_ID = 'dashboard-service-status';

/**
 * (2635) Account summary: the warnings bell on its header, the headline
 * counts (3133), then everything that used to be the whole dashboard: the
 * "Add New Service" entry point and the service sections (3134).
 *
 * @param {object} props
 * @param {(serviceProfile?: object) => void} props.onAddService - with a
 *   profile when "+ Add now" was used for it
 * @param {import('react').ReactNode} [props.notifications] - the warnings
 *   bell, on the header's right
 * @param {object} props.dashboardServicesProps - passed straight to DashboardServices
 */
const AccountSummary = ({
    onAddService,
    notifications,
    dashboardServicesProps,
}) => {
    const { darkMode } = useSelector((state) => state.settings);
    const { currentCompany } = useSelector((state) => state.navigationLists);
    const tokens = dashboardTokens(darkMode);
    const [showMoreServices, setShowMoreServices] = useBoolean(false);

    const { services, dashboardData } = dashboardServicesProps;
    const { counts, breakdowns } = useAccountSummaryCounts({
        companyId: currentCompany,
        services,
    });
    const { data: serviceProfiles } =
        useGetAvailableServiceProfiles(currentCompany);
    const availableServices = getAvailableServices(
        serviceProfiles,
        dashboardData?.services,
    );

    // Unknown while the first payload loads; the sections show their own
    // skeleton until then.
    const hasNoServices =
        Array.isArray(services) &&
        !services.length &&
        !dashboardData?.appAuths?.length;

    return (
        <DashboardPanel
            title="Account summary"
            icon={<IoPulse size={18} color={tokens.healthy} aria-hidden />}
            action={notifications}>
            <SummaryTiles counts={counts} breakdowns={breakdowns} />
            <Button
                onClick={() => onAddService()}
                variant="outline"
                width="100%"
                height="auto"
                mt={5}
                mb={3}
                py={2}
                fontSize="xs"
                fontWeight="semibold"
                borderStyle="dashed"
                borderWidth="1px"
                borderRadius="lg"
                borderColor={tokens.addService.border}
                bg={tokens.addService.bg}
                color={tokens.addService.color}
                _hover={{ bg: tokens.addService.hoverBg }}
                leftIcon={<IoAdd aria-hidden />}
                iconSpacing={1}
                data-walkthrough-id="/dashboard-services/card/add-service">
                Add New Service
            </Button>
            {!hasNoServices && (
                <Text
                    as="h3"
                    id={SERVICE_STATUS_ID}
                    fontSize="xs"
                    fontWeight="bold"
                    letterSpacing="wide"
                    mb={2}
                    color={tokens.muted}>
                    Service status
                </Text>
            )}
            <DashboardServices {...dashboardServicesProps} />
            {!hasNoServices && availableServices.length > 0 && (
                <Box>
                    {showMoreServices && (
                        <AvailableServices
                            services={availableServices}
                            onAddNow={onAddService}
                        />
                    )}
                    <chakra.button
                        type="button"
                        onClick={setShowMoreServices.toggle}
                        aria-expanded={showMoreServices}
                        w="full"
                        py={2}
                        fontSize="xs"
                        fontWeight="semibold"
                        color={tokens.addService.color}>
                        {showMoreServices
                            ? 'Show fewer services'
                            : 'Show more services'}
                    </chakra.button>
                </Box>
            )}
        </DashboardPanel>
    );
};

export default AccountSummary;
